import React from 'react';
import { useSelector } from 'react-redux';
import { useBi, useEnvironment, useTranslation } from '@wix/yoshi-flow-editor';
import { memberTabAdminActionDone } from '@wix/bi-logger-groups/v2';

import { Role } from '@wix/ambassador-social-groups-v2-group-role/types';

import type { IGroupMember } from 'api/members/types';
import { selectChangeRoleStatus } from 'store/selectors';

import { useController } from 'common/context/controller';

import { useDidUpdate } from 'common/hooks';
import { ProfileListItem } from 'common/components/ProfileListItem';

import { Box } from 'wui/Box';
import { Button } from 'wui/Button';
import { AlertDialog } from 'wui/AlertDialog';
import { DialogTitle } from 'wui/DialogTitle';
import { DialogActions } from 'wui/DialogActions';
import { DialogContent } from 'wui/DialogContent';
import { Typography } from 'wui/Typography';

export interface IProps {
  isOpen: boolean;
  groupId: string;
  member: IGroupMember;

  onClose(): void;
}

export function PromoteMemberDialog(props: IProps) {
  const { member, isOpen, onClose } = props;
  const isAdmin = member.role === Role.ADMIN;

  const bi = useBi();
  const { t } = useTranslation();
  const { members$ } = useController();
  const { isMobile } = useEnvironment();

  const status = useSelector(
    selectChangeRoleStatus(member.siteMemberId as string),
  );

  useDidUpdate(() => {
    if (!status.loading && !status.error) {
      onClose();
    }
  }, [status.loading, status.error]);

  return (
    <AlertDialog
      fullscreen={isMobile}
      isOpen={isOpen}
      onClose={onClose}
      aria-labelledby="pm-dialog-title"
    >
      <DialogTitle
        alert
        id="pm-dialog-title"
        title={
          isAdmin
            ? t('groups-web.group.actions.remove-admin-role.modal.title')
            : t('groups-web.group.actions.add-admin-role.modal.title')
        }
      />
      <DialogContent alert>
        <Box align="center">
          <ProfileListItem disableGutters profile={member} />
        </Box>

        <Typography>
          {isAdmin
            ? t(
                'groups-web.group.actions.remove-admin-role.modal.description',
                { memberFullName: member.name },
              )
            : t('groups-web.group.actions.add-admin-role.modal.description')}
        </Typography>

        <ul>
          <li>
            <Typography>
              {t('groups-web.group.actions.admin-role-description.posts')}
            </Typography>
          </li>
          <li>
            <Typography>
              {t('groups-web.group.actions.admin-role-description.comments')}
            </Typography>
          </li>
          <li>
            <Typography>
              {t('groups-web.group.actions.admin-role-description.members')}
            </Typography>
          </li>
        </ul>
      </DialogContent>
      <DialogActions alert>
        <Button outlined variant="basic" onClick={onClose} fullWidth={isMobile}>
          {t('groups-web.cancel')}
        </Button>
        <Button
          variant="basic"
          fullWidth={isMobile}
          onClick={handleSubmit}
          disabled={status.loading}
          loading={status.loading}
        >
          {isAdmin
            ? t('groups-web.group.actions.remove-admin-role.modal.confirm')
            : t('groups-web.group.actions.add-admin-role.modal.confirm')}
        </Button>
      </DialogActions>
    </AlertDialog>
  );

  function handleSubmit() {
    members$.changeRole(
      props.groupId,
      member.siteMemberId as string,
      isAdmin ? Role.MEMBER : Role.ADMIN,
    );

    bi.report(
      memberTabAdminActionDone({
        action: isAdmin ? 'remove_admin' : 'set_admin',
        group_id: props.groupId,
        origin: 'dialog_screen_btn_clk',
        site_member_id: member.siteMemberId as string,
      }),
    );
  }
}

PromoteMemberDialog.displayName = 'PromoteMemberDialog';
